"use client";
import { useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle, Search } from 'lucide-react';
import { FAQSchema } from '@/components/seo/SchemaMarkup';
import { Input } from '@/components/ui/input';

interface UnifiedFAQSectionProps {
  faqs: { question: string; answer: string }[];
  title?: string;
  subtitle?: string;
  enableSearch?: boolean;
  htmlAnswers?: boolean;
  showSchema?: boolean;
}

export function UnifiedFAQSection({
  faqs,
  title = "Frequently Asked Questions",
  subtitle,
  enableSearch,
  htmlAnswers = false,
  showSchema = true,
}: UnifiedFAQSectionProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const validFaqs = faqs.filter(faq =>
    faq.question && faq.question.trim().length > 3 &&
    faq.answer && faq.answer.trim().length > 0
  );

  const showSearch = enableSearch ?? validFaqs.length >= 8;

  const filteredFaqs = validFaqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (validFaqs.length === 0) return null;

  const schemaQuestions = validFaqs.map(faq => ({
    question: faq.question,
    answer: faq.answer.replace(/<[^>]*>/g, ''),
  }));

  return (
    <>
      {showSchema && <FAQSchema questions={schemaQuestions} />}
      <section className="py-16 bg-background">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {title}
            </h2>
            {subtitle && (
              <p className="text-lg text-muted-foreground">
                {subtitle}
              </p>
            )}
          </div>

          {showSearch && (
            <div className="mb-6 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          )}

          {filteredFaqs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No FAQs match your search. Try different keywords.
            </div>
          ) : (
            <Accordion type="single" collapsible className="w-full space-y-3">
              {filteredFaqs.map((faq, index) => (
                <AccordionItem
                  key={`faq-${index}-${faq.question.substring(0, 20)}`}
                  value={`item-${index}`}
                  className="border rounded-lg px-6 bg-background shadow-sm hover:shadow-md transition-shadow"
                >
                  <AccordionTrigger className="text-left text-base font-semibold hover:no-underline py-4">
                    {faq.question}
                  </AccordionTrigger>
                  {/* forceMount: closed answers stay in the HTML (Radix marks them `hidden`)
                      instead of being left out entirely. Crawlers read page text, and the
                      FAQPage schema emitted below must match content that is on the page. */}
                  <AccordionContent forceMount className="text-muted-foreground pt-2 pb-4">
                    {htmlAnswers ? (
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                      />
                    ) : (
                      <p className="leading-relaxed">{faq.answer}</p>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}

          {searchQuery && filteredFaqs.length > 0 && (
            <p className="text-sm text-muted-foreground mt-4 text-center">
              Showing {filteredFaqs.length} of {validFaqs.length} questions
            </p>
          )}
        </div>
      </section>
    </>
  );
}
